import api from './api';

export interface AppUser {
  user_id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  role?: string;
  is_active?: boolean;
  last_login?: string;
  created_at?: string;
  updated_at?: string;
  project_list?: string | null;
  offers_list?: string | null;
  position?: string | null;
  telefon?: string | null;
  /** Login mobilny (pracownik fizyczny) */
  mobile_login?: string | null;
  /** Wymuszona zmiana hasła przy pierwszym logowaniu w aplikacji */
  password_must_change?: boolean;
}

export type ModuleKey = 'home' | 'projects' | 'offers' | 'database' | 'estimates' | 'invoices' | 'analytics' | 'service' | 'permissions' | 'tasker';
export type PermissionsMap = Partial<Record<ModuleKey, boolean>>;

export const listUsers = async (): Promise<AppUser[]> => {
  const res = await api.get<{ success: boolean; data: AppUser[] }>('/users');
  return res.data.data || [];
};

export const listUsersPublic = async (): Promise<Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url' | 'role'>[]> => {
  const res = await api.get<{ success: boolean; data: Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url' | 'role'>[] }>('/users/all');
  return res.data.data || [];
};

/** Wyklucza konta pracowników terenowych (rola `field_worker`) z listy — do pól przypisania, nie do czatu. */
export function excludeFieldWorkerAccounts<T extends { role?: string | null }>(list: T[]): T[] {
  return list.filter((u) => u.role !== 'field_worker');
}

/**
 * Użytkownicy aplikacji do pól w ofertach, projektach, fakturach (bez pracowników fizycznych `field_worker`).
 * Pełna lista z `/users` (wymaga uprawnień); filtr po stronie klienta.
 */
export const listUsersForBusinessForms = async (): Promise<AppUser[]> => {
  return excludeFieldWorkerAccounts(await listUsers());
};

/**
 * Ta sama rola co `listUsersForBusinessForms`, ale przez `/users/all` (bez admina).
 * Dla formularzy opartych na publicznej liście (np. filtr „kierownik” na liście projektów).
 */
export const listUsersPublicForBusinessForms = async (): Promise<
  Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url' | 'role'>[]
> => {
  const res = await api.get<{ success: boolean; data: Pick<AppUser, 'user_id' | 'display_name' | 'email' | 'avatar_url' | 'role'>[] }>(
    '/users/all',
    { params: { forBusiness: 1 } }
  );
  return res.data.data || [];
};

export const updateUserRole = async (userId: string, role: string): Promise<void> => {
  await api.patch(`/users/${userId}/role`, { role });
};

export type CreateFieldWorkerPayload = {
  mobile_login: string;
  first_name: string;
  last_name: string;
  telefon?: string;
  position?: string;
  /** Opcjonalnie; jeśli puste, backend wygeneruje silne hasło */
  temporary_password?: string;
};

export type CreateFieldWorkerResult = {
  user_id: string;
  mobile_login: string;
  temporary_password: string;
  password_must_change: boolean;
};

export const createFieldWorker = async (payload: CreateFieldWorkerPayload): Promise<CreateFieldWorkerResult> => {
  const res = await api.post<{ success: boolean; data: CreateFieldWorkerResult }>('/users/field-worker', payload);
  if (!res.data?.success || !res.data.data) {
    throw new Error((res.data as any)?.message || 'Nie udało się utworzyć konta');
  }
  return res.data.data;
};

/** Usuwa konto użytkownika z rolą field_worker (tylko admin). */
export const deleteFieldWorker = async (userId: string): Promise<void> => {
  const res = await api.delete<{ success: boolean; message?: string }>(`/users/field-worker/${encodeURIComponent(userId)}`);
  if (!res.data?.success) {
    throw new Error((res.data as any)?.message || 'Nie udało się usunąć konta');
  }
};

export const getUserPermissions = async (userId: string): Promise<PermissionsMap> => {
  const res = await api.get<{ success: boolean; data: PermissionsMap }>(`/users/${userId}/permissions`);
  return res.data.data || {};
};

export const updateUserPermissions = async (userId: string, permissions?: PermissionsMap, project_list?: string, offers_list?: string): Promise<void> => {
  const body: any = {};
  if (permissions && Object.keys(permissions).length > 0) body.permissions = permissions;
  if (project_list !== undefined) body.project_list = project_list;
  if (offers_list !== undefined) body.offers_list = offers_list;
  await api.put(`/users/${userId}/permissions`, body);
};

// Activity summaries
export type PeriodKey = 'day' | 'week' | 'month' | 'halfyear' | 'year';

export async function getUserActivitySummary(userId: string, period: PeriodKey) {
  const path = period === 'day'
    ? '/activity'
    : period === 'week'
      ? '/activity/summary?period=week'
      : period === 'month'
        ? '/activity/summary?period=month'
        : period === 'halfyear'
          ? '/activity/summary?period=halfyear'
          : '/activity/summary?period=year';
  const params: any = { user_id: userId };
  if (period === 'day') {
    const today = new Date();
    const from = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const to = new Date(from.getTime() + 24 * 60 * 60 * 1000);
    params.from = from.toISOString();
    params.to = to.toISOString();
  }
  const res = await api.get(path, { params });
  return res.data?.data || [];
}

export async function getUserActivitySummaryByAction(userId: string, period: Exclude<PeriodKey, 'day'>) {
  const res = await api.get('/activity/summary-by-action', { params: { user_id: userId, period } });
  return res.data?.data || [];
}

export async function getUserPreferences(userId: string): Promise<{ preferred_calendar_ids?: string[]; preferred_tasklist_ids?: string[] }> {
  const res = await api.get<{ success: boolean; data: { preferred_calendar_ids?: string[]; preferred_tasklist_ids?: string[] } }>(`/users/${encodeURIComponent(userId)}/preferences`);
  return res.data?.data || { preferred_calendar_ids: [], preferred_tasklist_ids: [] };
}

export async function updateInitialBalance(initialBalance: number, initialBalanceDate: string): Promise<void> {
  await api.put('/auth/me', { initial_balance: initialBalance, initial_balance_date: initialBalanceDate });
}

export default {
  listUsers,
  listUsersPublic,
  listUsersForBusinessForms,
  listUsersPublicForBusinessForms,
  excludeFieldWorkerAccounts,
  updateUserRole,
  createFieldWorker,
  deleteFieldWorker,
  getUserPermissions,
  updateUserPermissions,
  getUserActivitySummary,
  getUserActivitySummaryByAction,
  getUserPreferences,
};
